/**
 * Lightweight hash-based SPA router with parameterized route support.
 * Routes: #/ (landing), #/sessions, #/videos, #/lessons, #/sim, #/lesson/:id
 */

type RouteHandler = (params?: Record<string, string>) => void;

interface RouteEntry {
  pattern: RegExp;
  paramNames: string[];
  handler: RouteHandler;
}

const routes: RouteEntry[] = [];
let started = false;

export function registerRoute(path: string, handler: RouteHandler): void {
  const paramNames: string[] = [];
  // Convert path like '/lesson/:id' into regex '/lesson/([^/]+)'
  const regexStr = path.replace(/:([^/]+)/g, (_match, paramName) => {
    paramNames.push(paramName);
    return '([^/]+)';
  });
  const pattern = new RegExp(`^${regexStr}$`);
  routes.push({ pattern, paramNames, handler });
}

export function navigate(path: string): void {
  window.location.hash = path;
}

function resolveRoute(): void {
  const hash = window.location.hash.replace('#', '') || '/';

  for (const route of routes) {
    const match = hash.match(route.pattern);
    if (match) {
      const params: Record<string, string> = {};
      route.paramNames.forEach((name, i) => {
        params[name] = match[i + 1];
      });
      route.handler(Object.keys(params).length > 0 ? params : undefined);
      return;
    }
  }

  // Fallback to landing
  const fallback = routes.find((r) => r.pattern.test('/'));
  if (fallback) fallback.handler();
}

export function startRouter(): void {
  if (started) return;
  started = true;

  window.addEventListener('hashchange', resolveRoute);

  // Resolve the initial route
  resolveRoute();
}
